// Native Studio Inc 1 — the publisher surface (host + cohosts). The app's FIRST
// media capture (PreJoin device select + preview, permission-denied handled via
// onError), then a CAPTURING LiveKitRoom (video/audio) rendering the BroadcastView
// panel (incl. self) + publish controls. Host-only: cohost management, mark-live,
// End broadcast.

import {
  ControlBar,
  LiveKitRoom,
  PreJoin,
  RoomAudioRenderer,
  StartAudio,
  useLocalParticipant,
  useRemoteParticipants,
  type LocalUserChoices,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { VideoPresets } from 'livekit-client';
import { useEffect, useState } from 'react';

import { AgendaPanel } from './AgendaPanel';
import { BroadcastView } from './BroadcastView';
import { LiveChatHostPanel } from './LiveChatHostPanel';
import { LiveServiceError, liveRoomService, makeIdentity } from './liveRoomService';
import { MessagingPanel } from './MessagingPanel';
import {
  designateCohost,
  listCohosts,
  markRoomLive,
  removeCohost,
  renameParticipant,
  updateRoomTicker,
} from './studioActions';
import { SuperchatQueue } from './SuperchatQueue';
import { TickerEditor } from './TickerEditor';
import { FeaturedSuperchatProvider } from './useFeaturedSuperchat';

import type { LiveRoomDetail } from './roomQueries';

import { useAuth } from '@/lib/auth/useAuth';

function StudioStatus({ label }: { label: string }) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <p className="font-sans text-sm text-video-text-faint">{label}</p>
    </div>
  );
}

export function StudioStage({ room }: { room: LiveRoomDetail }) {
  const { session, profile } = useAuth();
  const navigate = useNavigate();
  const [choices, setChoices] = useState<LocalUserChoices | null>(null);
  const [permError, setPermError] = useState<string | null>(null);

  // Mint AFTER PreJoin so the entered display name is baked into the token — it
  // becomes the on-stream name for every viewer, no client-side rename race.
  const tokenQuery = useQuery({
    queryKey: ['studio-token', room.livekitRoomName, session?.user.id, choices?.username],
    enabled: Boolean(choices && session),
    retry: false,
    staleTime: Infinity,
    queryFn: () =>
      liveRoomService.mintAccessToken(
        room.livekitRoomName,
        makeIdentity(session!.user.id),
        'host',
        choices!.username.trim() || undefined,
      ),
  });

  const role = tokenQuery.data?.role;
  const tokenError = tokenQuery.error;

  // Non-publishers (viewer / gated member) belong on the watch page.
  useEffect(() => {
    const gated = tokenError instanceof LiveServiceError && tokenError.code === 'must_subscribe';
    if (role === 'viewer' || role === 'guest' || gated) {
      void navigate({ to: '/live/$slug', params: { slug: room.slug }, replace: true });
    }
  }, [role, tokenError, room.slug, navigate]);

  // Host marks the room live on going on air (native publish doesn't fire the
  // ingress webhook), then starts the composite egress (recording + opt-in
  // simulcast). Both are idempotent (RLS / server listEgress), so re-mounts are
  // safe; egress failure must not block going live.
  useEffect(() => {
    if (role === 'host') {
      markRoomLive(room.id)
        .then(() => liveRoomService.startEgress(room.livekitRoomName))
        .catch((e) => console.error('go-live / egress start failed', e));
    }
  }, [role, room.id, room.livekitRoomName]);

  if (permError) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center">
        <h2 className="font-serif text-xl text-video-text">Camera &amp; mic needed</h2>
        <p className="max-w-md font-sans text-sm text-video-text-muted">
          ArcheTelos needs camera and microphone access to put you on the panel. Allow access in
          your browser, then try again. ({permError})
        </p>
        <button
          type="button"
          onClick={() => setPermError(null)}
          className="rounded-md bg-video-text px-5 py-2 font-sans text-sm font-medium text-video-bg"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!choices) {
    return (
      <div className="mx-auto max-w-2xl p-6" data-lk-theme="default">
        <h1 className="mb-4 font-serif text-2xl text-video-text">Studio — {room.title}</h1>
        <PreJoin
          joinLabel="Go on air"
          userLabel="Display name (shown on the stream)"
          defaults={{ username: profile?.display_name ?? profile?.username ?? '' }}
          onSubmit={(c) => setChoices(c)}
          onError={(e) => setPermError(e.message)}
        />
      </div>
    );
  }

  if (tokenQuery.isLoading) return <StudioStatus label="Connecting…" />;
  if (tokenError) {
    const gated = tokenError instanceof LiveServiceError && tokenError.code === 'must_subscribe';
    return <StudioStatus label={gated ? 'Redirecting…' : "Couldn't open the studio."} />;
  }
  if (!tokenQuery.data || (role !== 'host' && role !== 'cohost')) {
    return <StudioStatus label="Redirecting…" />;
  }

  return (
    <LiveKitRoom
      token={tokenQuery.data.token}
      serverUrl={tokenQuery.data.url}
      connect
      video={choices.videoEnabled}
      audio={choices.audioEnabled}
      options={{
        adaptiveStream: true,
        dynacast: true,
        // Capture 16:9 at the source (hard spec); object-cover crops any
        // remaining off-ratio (e.g. a portrait device) to fill the tile.
        videoCaptureDefaults: {
          deviceId: choices.videoDeviceId || undefined,
          resolution: VideoPresets.h720.resolution,
        },
        audioCaptureDefaults: { deviceId: choices.audioDeviceId || undefined },
        publishDefaults: { simulcast: true },
      }}
      data-lk-theme="default"
      className="flex min-h-screen flex-col bg-video-bg"
      onDisconnected={() => navigate({ to: '/' })}
    >
      {/* One shared featured-superchat instance backs both the on-air overlay
          (in BroadcastView) and the host queue below. */}
      <FeaturedSuperchatProvider>
        <div className="relative aspect-video w-full bg-black">
          <BroadcastView
            roomStatus="live"
            hostId={room.hostId}
            title={room.title}
            tickerLines={room.tickerLines}
            logoUrl={room.logoUrl}
          />
          {/* Surfaces a "Tap to unmute" button only while the browser blocks audio
            playback — without it cohosts can't hear each other. Auto-hides once
            audio is allowed. */}
          <StartAudio
            label="Tap to unmute"
            className="absolute bottom-4 right-4 rounded-md bg-video-bg/70 px-3 py-1.5 font-sans text-xs text-video-text"
          />
        </div>
        <ControlBar
          variation="minimal"
          controls={{ microphone: true, camera: true, screenShare: true, leave: true }}
        />
        <RoomAudioRenderer />
        <LeaveOnUnload roomName={room.livekitRoomName} />
        {(role === 'host' || role === 'cohost') && (
          <HostControls room={room} role={role} canEnd={role === 'host'} />
        )}
      </FeaturedSuperchatProvider>
    </LiveKitRoom>
  );
}

// On tab/window close, LiveKit's own disconnect is async and often doesn't flush
// before the page tears down, so the SFU keeps the publisher until its timeout
// (~15s) and their tile lingers for everyone. A sendBeacon on pagehide survives
// unload and force-removes them server-side immediately (self-removal: the
// function only honors the caller's own identity). SPA navigation away is already
// handled by LiveKitRoom unmount → room.disconnect().
export function LeaveOnUnload({ roomName }: { roomName: string }) {
  const { session } = useAuth();
  const { localParticipant } = useLocalParticipant();
  const token = session?.access_token;
  const identity = localParticipant?.identity;
  useEffect(() => {
    if (!token || !identity) return;
    const leave = () => {
      navigator.sendBeacon(
        `${import.meta.env.PUBLIC_SUPABASE_URL}/functions/v1/room-leave?beacon=1`,
        new Blob([JSON.stringify({ roomId: roomName, identity, token })], {
          type: 'application/json',
        }),
      );
    };
    window.addEventListener('pagehide', leave);
    return () => window.removeEventListener('pagehide', leave);
  }, [roomName, identity, token]);
  return null;
}

function CopyLink({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard unavailable */
        }
      }}
      className="rounded-md border border-video-line px-3 py-1.5 font-sans text-xs text-video-text-muted hover:text-video-text"
    >
      {copied ? 'Copied' : label}
    </button>
  );
}

function HostControls({
  room,
  role,
  canEnd,
}: {
  room: LiveRoomDetail;
  role: 'host' | 'cohost';
  canEnd: boolean;
}) {
  const { session } = useAuth();
  const hostId = session?.user.id ?? '';
  const qc = useQueryClient();
  const navigate = useNavigate();
  const participants = useRemoteParticipants();
  const [handle, setHandle] = useState('');
  const [confirmEnd, setConfirmEnd] = useState(false);

  const cohosts = useQuery({
    queryKey: ['room-cohosts', room.id],
    queryFn: () => listCohosts(room.id),
  });
  const add = useMutation({
    mutationFn: () => designateCohost(room.id, handle, hostId),
    onSuccess: () => {
      setHandle('');
      void qc.invalidateQueries({ queryKey: ['room-cohosts', room.id] });
    },
  });
  const remove = useMutation({
    mutationFn: (memberId: string) => removeCohost(room.id, memberId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['room-cohosts', room.id] }),
  });
  const end = useMutation({
    mutationFn: () => liveRoomService.endRoom(room.livekitRoomName),
    onSuccess: () => navigate({ to: '/' }),
  });
  const ticker = useMutation({
    mutationFn: (lines: string[]) => updateRoomTicker(room.id, lines),
  });

  const studioUrl =
    typeof window !== 'undefined' ? `${window.location.origin}/studio/${room.slug}` : '';

  return (
    <section className="space-y-5 border-t border-video-line p-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-serif text-lg text-video-text">Controls</h2>
        <CopyLink value={studioUrl} label="Copy cohost link" />
        <a
          href={`/live/${room.slug}`}
          target="_blank"
          rel="noreferrer"
          className="font-sans text-sm text-video-text-muted hover:text-video-text hover:underline"
        >
          View public page ↗
        </a>
        <div className="ml-auto">
          {!canEnd ? null : confirmEnd ? (
            <span className="flex items-center gap-2">
              <span className="font-sans text-xs text-video-text-muted">End broadcast?</span>
              <button
                type="button"
                onClick={() => end.mutate()}
                disabled={end.isPending}
                className="rounded-md bg-oxblood-live px-3 py-1.5 font-sans text-xs font-medium text-video-text disabled:opacity-50"
              >
                {end.isPending ? 'Ending…' : 'End now'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmEnd(false)}
                className="rounded-md border border-video-line px-3 py-1.5 font-sans text-xs text-video-text-muted"
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmEnd(true)}
              className="rounded-md border border-video-line px-4 py-1.5 font-sans text-sm text-video-text hover:border-video-line-strong"
            >
              End broadcast
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Column 1 — team, on-air names, ticker */}
        <div className="space-y-5">
          <div className="space-y-3">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.12em] text-video-text-faint">
              Cohosts
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (handle.trim()) add.mutate();
              }}
              className="flex gap-2"
            >
              <input
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                placeholder="member username"
                className="min-w-0 flex-1 rounded-md border border-video-line bg-video-inset px-3 py-2 font-sans text-sm text-video-text outline-none focus:border-video-line-strong"
              />
              <button
                type="submit"
                disabled={!handle.trim() || add.isPending}
                className="rounded-md bg-video-text px-4 py-2 font-sans text-sm font-medium text-video-bg disabled:opacity-50"
              >
                {add.isPending ? 'Adding…' : 'Designate'}
              </button>
            </form>
            {add.isError && (
              <p className="font-sans text-xs text-oxblood-live">
                {add.error instanceof Error ? add.error.message : 'Could not designate that member'}
              </p>
            )}
            <ul className="space-y-1">
              {(cohosts.data ?? []).map((c) => (
                <li key={c.memberId} className="flex items-center gap-2 font-sans text-sm">
                  <span className="min-w-0 flex-1 truncate text-video-text">
                    {c.displayName}
                    {c.username ? (
                      <span className="text-video-text-faint"> @{c.username}</span>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    onClick={() => remove.mutate(c.memberId)}
                    className="font-sans text-xs text-video-text-faint hover:text-oxblood-live"
                  >
                    Remove
                  </button>
                </li>
              ))}
              {cohosts.data?.length === 0 && (
                <li className="font-sans text-xs text-video-text-faint">
                  No cohosts yet — designate a member by username and share the cohost link.
                </li>
              )}
            </ul>
          </div>

          <div className="space-y-3">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.12em] text-video-text-faint">
              On air — names shown on the stream
            </p>
            {participants.length === 0 ? (
              <p className="font-sans text-xs text-video-text-faint">
                No one else on the panel yet.
              </p>
            ) : (
              <ul className="space-y-2">
                {participants.map((p) => (
                  <RenameRow
                    key={p.identity}
                    roomId={room.livekitRoomName}
                    identity={p.identity}
                    currentName={p.name || p.identity}
                  />
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-2">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.12em] text-video-text-faint">
              Ticker (live)
            </p>
            <TickerEditor
              initial={room.tickerLines}
              onSave={(lines) => ticker.mutate(lines)}
              saving={ticker.isPending}
            />
          </div>
        </div>

        {/* Column 2 — agenda */}
        <AgendaPanel roomId={room.id} />

        {/* Column 3 — live chat (moderation), superchat queue, team messaging */}
        <div className="space-y-6">
          <div className="h-96">
            <LiveChatHostPanel roomId={room.id} role={role} />
          </div>
          <SuperchatQueue />
          <MessagingPanel />
        </div>
      </div>
    </section>
  );
}

function RenameRow({
  roomId,
  identity,
  currentName,
}: {
  roomId: string;
  identity: string;
  currentName: string;
}) {
  const [name, setName] = useState(currentName);
  const rename = useMutation({
    mutationFn: () => renameParticipant(roomId, identity, name.trim()),
  });
  return (
    <li className="flex items-center gap-2">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={60}
        aria-label="On-stream name"
        className="min-w-0 flex-1 rounded-md border border-video-line bg-video-inset px-2 py-1 font-sans text-sm text-video-text"
      />
      <button
        type="button"
        onClick={() => rename.mutate()}
        disabled={!name.trim() || rename.isPending}
        className="shrink-0 rounded-md border border-video-line px-3 py-1 font-sans text-xs text-video-text-muted hover:text-video-text disabled:opacity-50"
      >
        {rename.isPending ? 'Saving…' : rename.isSuccess ? 'Saved' : 'Rename'}
      </button>
    </li>
  );
}
